import React, { useState, useEffect } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Input, FormGroup, Label, Spinner } from 'reactstrap';
import { NeoButton } from '../../../../../components/Common';

const BLOCKER_ICON = {
    enrollment: 'mdi-school-outline',
    membership: 'mdi-card-account-details-outline',
    financial: 'mdi-cash-clock',
};

/**
 * Modal de exclusão de membro com:
 * 1. Verificação de bloqueadores (matrículas/planos/faturas ativas)
 * 2. Confirmação com senha do owner
 */
const DeleteMemberModal = ({ isOpen, toggle, memberName, blockers, blockersLoading, onConfirm }) => {
    const [password, setPassword] = useState('');
    const [confirming, setConfirming] = useState(false);
    const [pwError, setPwError] = useState('');

    const hasBlockers = blockers && blockers.length > 0;

    useEffect(() => {
        if (!isOpen) {
            setPassword('');
            setPwError('');
            setConfirming(false);
        }
    }, [isOpen]);

    const handleConfirm = async () => {
        if (!password.trim()) {
            setPwError('Digite sua senha para confirmar.');
            return;
        }
        setPwError('');
        setConfirming(true);
        try {
            await onConfirm(password);
        } catch (err) {
            const code = err?.code || '';
            if (code === 'auth/wrong-password' || code === 'auth/invalid-credential') {
                setPwError('Senha incorreta. Tente novamente.');
            } else if (code === 'auth/too-many-requests') {
                setPwError('Muitas tentativas. Aguarde alguns minutos.');
            } else {
                setPwError(err?.message || 'Erro ao confirmar. Tente novamente.');
            }
        } finally {
            setConfirming(false);
        }
    };

    return (
        <Modal isOpen={isOpen} toggle={toggle} centered size="md">
            <ModalHeader
                toggle={toggle}
                style={{ borderBottom: '2px solid #fee2e2', background: '#fff5f5' }}
            >
                <span className="fw-bold" style={{ color: '#991b1b', fontFamily: 'Outfit, sans-serif' }}>
                    <i className="mdi mdi-delete-alert-outline me-2"></i>
                    Excluir Membro
                </span>
            </ModalHeader>

            <ModalBody style={{ fontFamily: 'Outfit, sans-serif' }}>
                {blockersLoading ? (
                    <div className="text-center py-4">
                        <Spinner color="danger" size="sm" className="me-2" />
                        <span className="text-muted font-size-13">Verificando vínculos ativos...</span>
                    </div>
                ) : hasBlockers ? (
                    <>
                        <div className="d-flex align-items-start gap-3 mb-4 p-3 rounded-3"
                            style={{ background: '#fef2f2', border: '1.5px solid #fca5a5' }}>
                            <i className="mdi mdi-alert-circle font-size-22 mt-1" style={{ color: '#dc2626', flexShrink: 0 }}></i>
                            <div>
                                <p className="fw-bold mb-1 font-size-14" style={{ color: '#991b1b' }}>
                                    Não é possível excluir <strong>{memberName}</strong>
                                </p>
                                <p className="mb-0 font-size-13 text-muted">
                                    Encerre os vínculos abaixo antes de prosseguir com a exclusão.
                                </p>
                            </div>
                        </div>

                        <p className="fw-semibold font-size-12 text-muted text-uppercase mb-2" style={{ letterSpacing: '0.06em' }}>
                            Vínculos ativos que impedem a exclusão
                        </p>
                        <div className="d-flex flex-column gap-2">
                            {blockers.map((b, i) => (
                                <div key={i} className="d-flex align-items-center gap-3 p-3 rounded-3"
                                    style={{ background: '#fff', border: '1px solid #fca5a5' }}>
                                    <i className={`mdi ${BLOCKER_ICON[b.type] || 'mdi-alert'} font-size-18`} style={{ color: '#dc2626' }}></i>
                                    <div>
                                        <span className="fw-bold font-size-13" style={{ color: '#991b1b' }}>{b.label}</span>
                                        {b.detail && (
                                            <span className="d-block font-size-12 text-muted">{b.detail}</span>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </>
                ) : (
                    <>
                        <div className="d-flex align-items-start gap-3 mb-4 p-3 rounded-3"
                            style={{ background: '#fff7ed', border: '1.5px solid #fed7aa' }}>
                            <i className="mdi mdi-information-outline font-size-22 mt-1" style={{ color: '#c2410c', flexShrink: 0 }}></i>
                            <div>
                                <p className="fw-bold mb-1 font-size-14" style={{ color: '#9a3412' }}>
                                    Excluir <strong>{memberName}</strong>?
                                </p>
                                <p className="mb-0 font-size-13 text-muted">
                                    O membro será removido de todas as listas e sessões futuras. O histórico financeiro e de frequência será preservado.
                                    <br />
                                    <strong>Esta ação não pode ser desfeita.</strong>
                                </p>
                            </div>
                        </div>

                        <div className="d-flex align-items-center gap-2 mb-3 px-3 py-2 rounded-3"
                            style={{ background: '#f1f5f9', border: '1px solid #cbd5e1' }}>
                            <i className="mdi mdi-shield-account-outline font-size-16" style={{ color: '#475569', flexShrink: 0 }}></i>
                            <span className="font-size-12 text-muted">
                                Apenas <strong>proprietários</strong> e <strong>gestores</strong> podem excluir membros.
                            </span>
                        </div>

                        <FormGroup className="mb-0">
                            <Label className="fw-semibold font-size-13 mb-1">
                                <i className="mdi mdi-lock-outline me-1" style={{ color: '#9a3412' }}></i>
                                Confirme sua senha para prosseguir
                            </Label>
                            {/* Input oculto absorve o preenchimento automático do browser (username) */}
                            <input type="text" autoComplete="username" style={{ display: 'none' }} readOnly tabIndex={-1} />
                            <Input
                                type="password"
                                placeholder="Digite sua senha"
                                value={password}
                                onChange={e => { setPassword(e.target.value); setPwError(''); }}
                                onKeyDown={e => { if (e.key === 'Enter') handleConfirm(); }}
                                invalid={!!pwError}
                                autoComplete="new-password"
                                autoFocus
                                style={{ borderRadius: '10px' }}
                            />
                            {pwError && (
                                <div className="invalid-feedback d-block font-size-12">{pwError}</div>
                            )}
                        </FormGroup>
                    </>
                )}
            </ModalBody>

            <ModalFooter style={{ borderTop: '1px solid #fee2e2', gap: '8px' }}>
                <NeoButton variant="outline-secondary" onClick={toggle} size="sm">
                    Cancelar
                </NeoButton>
                {!blockersLoading && !hasBlockers && (
                    <NeoButton
                        variant="danger"
                        onClick={handleConfirm}
                        size="sm"
                        disabled={confirming || !password.trim()}
                        icon={confirming ? undefined : 'mdi-delete-outline'}
                    >
                        {confirming ? <><Spinner size="sm" className="me-2" />Excluindo...</> : 'Confirmar Exclusão'}
                    </NeoButton>
                )}
            </ModalFooter>
        </Modal>
    );
};

export default DeleteMemberModal;
